import { ReactElement, ReactNode } from 'react'
import { GoogleTagManager } from '@next/third-parties/google'
import { NextPage } from 'next'
import { type AppContext, type AppProps, default as NextApp } from 'next/app'
import dynamic from 'next/dynamic'

import Loader from '@/components/base/loader'
import SVGSprites from '@/components/base/svg-sprites'
import { Store, StoreProvider } from '@/contexts/store/'
import { ThemeProvider } from '@/contexts/theme'
import { TransitionProvider } from '@/contexts/transition'
import { getPromiseFromFetch } from '@/utils/request'
import { API, GTM_ID, MOCK_DATA } from '@/constants'

import '@/styles/globals.css'
import 'plyr/dist/plyr.css'

import '@/base/gsap'

const GridHelper = dynamic(() => import('@/components/base/grid-helper'), {
  ssr: false
})

const CookieBanner = dynamic(() => import('@/components/base/cookie-banner'), {
  ssr: false
})

const Stage = dynamic(() => import('@/components/base/stage'), {
  ssr: false
})

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement, props: AppProps) => ReactNode
}

export default function App({
  Component,
  pageProps,
  menu,
  strings
}: AppProps & Store & { Component: NextPageWithLayout }) {
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <>
      <GoogleTagManager gtmId={GTM_ID} />
      <StoreProvider menu={menu} strings={strings}>
        <ThemeProvider fluid theme={pageProps?.acf?.page_settings}>
          {!process.env.production ? <GridHelper /> : null}
          <Stage />
          <TransitionProvider>
            {getLayout(<Component {...pageProps} />, pageProps)}
          </TransitionProvider>
          <Loader />
          <CookieBanner />
          <SVGSprites />
        </ThemeProvider>
      </StoreProvider>
    </>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)
  const lang = appContext.router.locale

  const { data: menuData } = MOCK_DATA
    ? { data: require(`@/data/menu_locations.json`) }
    : await getPromiseFromFetch({
        method: 'get',
        url: `${API.MENU_LOCATIONS}?lang=${lang}`
      })

  const { data: optionsData } = MOCK_DATA
    ? { data: require(`@/data/options.json`) }
    : await getPromiseFromFetch({
        method: 'get',
        url: `${API.STRINGS}?lang=${lang}`
      })

  return {
    ...appProps,
    strings: optionsData,
    menu: Object.keys(menuData).length > 0 ? menuData : {}
  }
}
